/**
 * Vuex module for contacts
 */
import debug from 'debug'
import API from '@/api'
import Contact from 'Store/class/contact/contact'
import events from 'Store/events'

// sub modules
import tags from './tags'
import personalTags from './personal_tags'
import tagForm from './tag_form'
import tagDeleteForm from './tag_delete_form'
import detailsDialog from './details_dialog'
import deleteForm from './delete_form'
import importForm from './import_form'
import form from './form'
import phoneNumbers from './phone_numbers'

const dbg = debug('store:contacts:')

const actions = {
  get ({ commit, rootGetters, state }) {
    dbg('loading contacts')
    if (import.meta.env.NODE_ENV === 'test') {
      return true
    }
    return new Promise((resolve, reject) => {
      return API.getContacts(state.pagination.start, state.pagination.perPage, rootGetters['search/searchText'], state.isPersonal, state.filterByTags, state.filterByPersonalTags, state.pagination.sort_column, state.pagination.sort_direction)
        .then(response => {
          commit('ADD_CONTACTS', response.data.data)
          commit('SET_TOTAL', response.data.total)
          resolve()
        })
        .catch(err => {
          dbg(err)
          const msg = API.getException(err) || 'Failed to get contacts'
          events.pubError(msg)
          reject(err)
        })
    })
  },

  create ({ commit }, contact) {
    if (import.meta.env.NODE_ENV === 'test') {
      return true
    }
    commit('NEW_CONTACT', contact)
  },

  update ({ commit }, contact) {
    if (import.meta.env.NODE_ENV === 'test') {
      return true
    }
    commit('UPDATE_CONTACT', contact)
  },

  delete ({ commit }, contact) {
    if (import.meta.env.NODE_ENV === 'test') {
      return true
    }
    commit('DELETE_CONTACT', contact)
  },

  bulkDelete: async ({ dispatch }, contacts) => {
    const ids = contacts.map((contact) => contact.id)
    dbg('Delete contacts')
    try {
      await API.contactsBulkDelete(ids)
      events.pubNotify({
        status: 'success',
        text: 'contacts.notify.bulkDelOk',
        args: { count: ids.length }
      })
      await dispatch('get')
    } catch (err) {
      dbg(err)
      events.pubError(API.getException(err) || 'Failed to delete contacts with API')
    }
  },

  import ({ commit }, file) {
    if (import.meta.env.NODE_ENV === 'test') {
      return true
    }
    commit('IMPORT_CONTACTS', file)
  },

  setPage: function ({ commit }, page) {
    commit('SET_PAGE', page)
  },

  setPerPage ({ commit }, perPage) {
    commit('SET_PER_PAGE', perPage)
  },

  setIsPersonal ({ commit }, isPersonal) {
    commit('SET_IS_PERSONAL', isPersonal)
  },

  setFilterByTags ({ commit }, filterByTags) {
    commit('SET_FILTER_BY_TAGS', filterByTags)
  },

  setFilterByPersonalTags ({ commit }, filterByPersonalTags) {
    commit('SET_FILTER_BY_PERSONAL_TAGS', filterByPersonalTags)
  },

  setSort: function ({ commit, dispatch }, data) {
    commit('SET_SORT', data)
    dispatch('get')
  }
}

const mutations = {
  ADD_CONTACTS (state, data) {
    const contacts = []
    data.forEach(contact => {
      contacts.push(new Contact(contact))
    })
    state.contacts = contacts
  },

  NEW_CONTACT ({ state, rootState, rootGetters }, contact) {
    API.createContact(contact).then(resp => {
      events.pubNotify({
        status: 'success',
        text: 'contacts.notify.createdOk'
      })
      this.dispatch('contacts/get')
      this.dispatch('contacts/phoneNumbers/get')
      this.dispatch('chat/getSmsMessages')
    }).catch(err => {
      dbg(err.response)
      events.pubError(API.getException(err) || 'Failed to create contact')
    })
  },

  UPDATE_CONTACT (state, contact) {
    API.updateContact(contact).then(resp => {
      events.pubNotify({
        status: 'success',
        text: 'contacts.notify.updatedOk'
      })
      this.dispatch('contacts/get')
      this.dispatch('contacts/phoneNumbers/get')
      this.dispatch('contacts/detailsDialog/close')
    }).catch(err => {
      dbg(err.response)
      events.pubError(API.getException(err) || 'Failed to update contact')
    })
  },

  DELETE_CONTACT (state, contact) {
    API.deleteContact(contact).then(resp => {
      events.pubNotify({
        status: 'success',
        text: 'contacts.notify.deletedOk'
      })
      this.dispatch('contacts/get')
      this.dispatch('contacts/phoneNumbers/get')
      this.dispatch('contacts/detailsDialog/close')
    }).catch(err => {
      dbg(err.response)
      events.pubError(API.getException(err) || 'Failed to delete contact')
    })
  },

  IMPORT_CONTACTS (state, file) {
    API.importContacts(file).then(resp => {
      this.dispatch('contacts/get')
      this.dispatch('contacts/detailsDialog/close')
    }).catch(err => {
      dbg(err.response)
      events.pubError(API.getException(err) || 'Failed to import contacts')
    })
  },

  SET_PER_PAGE (state, perPage) {
    state.pagination.perPage = perPage
  },

  SET_PAGE (state, page) {
    page = page || state.pagination.curPage
    state.pagination.curPage = page
    state.pagination.start = (page - 1) * state.pagination.perPage
  },

  SET_TOTAL (state, total) {
    state.pagination.total = total
    state.pagination.pages = Math.ceil(total / state.pagination.perPage)
  },

  SET_IS_PERSONAL (state, isPersonal) {
    state.isPersonal = isPersonal
  },

  SET_FILTER_BY_TAGS (state, filterByTags) {
    state.filterByTags = filterByTags
  },

  SET_FILTER_BY_PERSONAL_TAGS (state, filterByPersonalTags) {
    state.filterByPersonalTags = filterByPersonalTags
  },

  SET_SORT (state, data) {
    state.pagination.sort_column = data.sort_column
    state.pagination.sort_direction = data.sort_direction
  }
}

const state = {
  contacts: [],
  isPersonal: false,
  filterByTags: [],
  filterByPersonalTags: [],
  pagination: {
    total: 0,
    perPage: 18,
    curPage: 1,
    pages: 0,
    start: 0,
    sort_column: 'name',
    sort_direction: 'ASC'
  }
}

const getters = {
  contacts: state => state.contacts,
  isPersonal: state => state.isPersonal,
  filterByTags: state => state.filterByTags,
  filterByPersonalTags: state => state.filterByPersonalTags,
  pagination: state => state.pagination,
  total: state => state.pagination.total
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
  modules: {
    tags,
    personalTags,
    tagForm,
    tagDeleteForm,
    detailsDialog,
    deleteForm,
    importForm,
    form,
    phoneNumbers
  }
}
